// My bookings list. Markup mirrors 10-my-bookings.html: .bookings-wrap with
// .page-head, .filter-chip-row of qpills, .bookings-list with .booking-row
// (head row + data rows), .pagination at the bottom.

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";
import { lookupAirport } from "../lib/airports";
import { useBodyClass } from "../lib/useBodyClass";
import { SkeletonBookingRow } from "../components/Skeletons";
import type { BookingListItem, BookingStatus, Paginated } from "../lib/types";

const PAGE_SIZE = 25;

type FilterKey = "" | "confirmed" | "upcoming" | "completed" | "drafts" | "refunded";
const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: "",          label: "All bookings" },
  { key: "confirmed", label: "Confirmed" },
  { key: "upcoming",  label: "Upcoming" },
  { key: "completed", label: "Completed" },
  { key: "drafts",    label: "Drafts & expired" },
  { key: "refunded",  label: "Refunded" },
];

const STATUS_PILL: Record<BookingStatus, { cls: string; label: string }> = {
  draft:            { cls: "draft",     label: "Draft" },
  passenger_saved:  { cls: "pending",   label: "Payment pending" },
  priced:           { cls: "pending",   label: "Repriced" },
  confirmed:        { cls: "confirmed", label: "Confirmed" },
  cancelled:        { cls: "refunded",  label: "Cancelled" },
  failed:           { cls: "failed",    label: "Failed · refunded" },
};

export function MyBookings() {
  useBodyClass("has-bg2");
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState<FilterKey>("");

  const q = useQuery({
    queryKey: ["bookings", offset, filter],
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      // Backend supports `status=` if we add it later; we filter client-side for now.
      return api.get<Paginated<BookingListItem>>(`/v1/bookings/?${params}`);
    },
  });

  const visibleRows = (q.data?.results || []).filter((b) => matches(b, filter));

  return (
    <div className="container">
      <div className="bookings-wrap">
        <div className="page-head">
          <h1>My <span className="serif">bookings</span>.</h1>
          <p className="lede">Every flight you've booked, drafted, or canceled — confirmed PNRs on top, expired drafts hidden in the filter.</p>
        </div>

        <div className="filter-chip-row">
          {FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              type="button"
              className={`qpill${filter === f.key ? " on" : ""}`}
              onClick={() => { setFilter(f.key); setOffset(0); }}
            >{f.label}</button>
          ))}
        </div>

        {q.isLoading && (
          <div className="bookings-list">
            <SkeletonBookingRow />
            <SkeletonBookingRow />
            <SkeletonBookingRow />
            <SkeletonBookingRow />
          </div>
        )}
        {q.error && <div className="app-error">Couldn't load bookings.</div>}

        {q.data && (
          <>
            <div className="bookings-list">
              <div className="booking-row head">
                <span>Booking</span>
                <span>Route</span>
                <span>Passengers</span>
                <span>Booked at</span>
                <span>Status</span>
                <span style={{ textAlign: "right" }}>Amount</span>
                <span />
              </div>

              {visibleRows.length === 0 && (
                <div className="booking-row" style={{ display: "block", textAlign: "center", color: "var(--ink-3)" }}>
                  No bookings match this filter.{" "}
                  <Link to="/" style={{ textDecoration: "underline", color: "var(--ink-2)" }}>Start a search.</Link>
                </div>
              )}

              {visibleRows.map((b) => {
                const pill = STATUS_PILL[b.status] || STATUS_PILL.draft;
                const route = describeRoute(b);
                const passengerNames = (b.passengers || [])
                  .map((p) => `${p.title ?? ""} ${p.first_name ?? ""} ${p.last_name ?? ""}`.trim())
                  .filter(Boolean);
                const summaryPax = passengerNames.length
                  ? `${passengerNames[0]}${passengerNames.length > 1 ? ` + ${passengerNames.length - 1}` : ""}`
                  : `${(b.passengers || []).length || "?"} pax`;
                const created = new Date(b.created_at);
                return (
                  <Link to={`/bookings/${b.id}`} className="booking-row" key={b.id}>
                    <div className="b-pnr">
                      <b>{b.pnr || "Draft"}</b>
                      <span title={b.id} style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                        {b.id.slice(0, 8)}…{b.id.slice(-4)}
                      </span>
                    </div>
                    <div className="b-route">
                      <b>{route.cities || "—"}</b>
                      <span>{route.codes || ""}</span>
                    </div>
                    <div className="b-pax">
                      {summaryPax}
                    </div>
                    <div className="b-pax">
                      {fmtBookedAt(created)}
                      <span style={{ fontSize: 11, color: "var(--ink-4)" }}>{fmtRelative(created)}</span>
                    </div>
                    <span><span className={`status-pill ${pill.cls}`}><span className="dot" />{pill.label}</span></span>
                    <span className="b-amt">{b.amount_charged ? fmtMoney(b.amount_charged, b.currency) : "—"}</span>
                    <span className="b-go">→</span>
                  </Link>
                );
              })}
            </div>

            <Pagination
              count={q.data.count}
              offset={offset}
              pageSize={PAGE_SIZE}
              onChange={setOffset}
            />
          </>
        )}
      </div>
    </div>
  );
}

function Pagination(props: { count: number; offset: number; pageSize: number; onChange: (o: number) => void }) {
  const { count, offset, pageSize, onChange } = props;
  if (count <= pageSize) return null;
  const totalPages = Math.ceil(count / pageSize);
  const currentPage = Math.floor(offset / pageSize) + 1;
  const pages = visiblePageNumbers(currentPage, totalPages);
  return (
    <div className="pagination">
      <span className="info">Showing <b>{offset + 1}–{Math.min(offset + pageSize, count)}</b> of {count}</span>
      <div className="pager">
        <button type="button" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - pageSize))}>←</button>
        {pages.map((p, idx) =>
          p === "…"
            ? <button key={`gap-${idx}`} className="gap" type="button" disabled>…</button>
            : <button key={p} type="button" className={currentPage === p ? "on" : undefined} onClick={() => onChange((p - 1) * pageSize)}>{p}</button>
        )}
        <button type="button" disabled={offset + pageSize >= count} onClick={() => onChange(offset + pageSize)}>→</button>
      </div>
    </div>
  );
}

function visiblePageNumbers(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "…", total];
  if (current >= total - 3) return [1, "…", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "…", current - 1, current, current + 1, "…", total];
}

function matches(b: BookingListItem, f: FilterKey): boolean {
  if (!f) return true;
  if (f === "confirmed") return b.status === "confirmed";
  if (f === "upcoming")  return b.status === "confirmed";  // would refine if we knew departure dates
  if (f === "completed") return b.status === "confirmed";  // ditto
  if (f === "drafts")    return b.status === "draft" || b.status === "passenger_saved" || b.status === "priced";
  if (f === "refunded")  return b.status === "cancelled" || b.status === "failed";
  return true;
}

// flight_segments is now stored as a list of {direction, segments} entries
// (populated at booking creation in flights/views.py). Older bookings stored
// a flat segment list — handle both shapes defensively.
function describeRoute(b: BookingListItem): { cities: string; codes: string } {
  const raw = (b.flight_segments || []) as Array<Record<string, unknown>>;
  if (raw.length === 0) return { cities: "", codes: "" };

  // Normalise to a flat array of segment dicts.
  const flat: Array<Record<string, unknown>> = [];
  for (const entry of raw) {
    if (entry && Array.isArray((entry as { segments?: unknown }).segments)) {
      for (const s of (entry as { segments: Array<Record<string, unknown>> }).segments) flat.push(s);
    } else if (entry && "origin" in entry) {
      flat.push(entry);
    }
  }
  if (flat.length === 0) return { cities: "", codes: "" };

  const codes: string[] = [];
  for (const s of flat) {
    const o = (s.origin as Record<string, unknown> | undefined)?.airport_code as string | undefined;
    const d = (s.destination as Record<string, unknown> | undefined)?.airport_code as string | undefined;
    if (o && (codes.length === 0 || codes[codes.length - 1] !== o)) codes.push(o);
    if (d) codes.push(d);
  }
  if (codes.length === 0) return { cities: "", codes: "" };
  const cities = codes.map((c) => lookupAirport(c).city).join(" → ");
  const tripType = codes.length >= 3 && codes[0] === codes[codes.length - 1] ? "round-trip" : "one-way";
  return { cities, codes: `${codes.join(" → ")} · ${tripType}` };
}

function fmtBookedAt(d: Date): string {
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtRelative(d: Date): string {
  if (Number.isNaN(d.getTime())) return "";
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} hr ago`;
  const days = Math.floor(secs / 86400);
  if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
  return "";
}
function fmtMoney(amount: string, currency: string): string {
  const n = parseFloat(amount);
  if (!Number.isFinite(n)) return "—";
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(n); }
  catch { return `${currency} ${Math.round(n).toLocaleString("en-IN")}`; }
}
